import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2, ShieldCheck } from "lucide-react";

const GRADES = ["K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
const blankStudent = () => ({ name: "", grade_level: "K", dob: "" });

export default function CoverSchoolEnroll() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    parent1_first: "", parent1_last: "", parent2_first: "", parent2_last: "",
    phone: "", address: "", city: "", state: "AL", zip: "", county: "", previous_school: "",
  });
  const [students, setStudents] = useState([blankStudent()]);
  const set = (k, v) => setF({ ...f, [k]: v });
  const setStudent = (i, k, v) => setStudents(students.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.parent1_first || !f.parent1_last || !f.phone || !f.address || !f.city || !f.zip) {
      toast.error("Please complete all required family fields."); return;
    }
    if (students.some((s) => !s.name.trim())) { toast.error("Enter a name for each student."); return; }
    setBusy(true);
    try {
      await api.post("/cover-school/enroll", { ...f, students });
      const { data } = await api.post("/cover-school/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-5 py-14 lg:px-8">
      <div className="flex items-center gap-2 text-primary"><ShieldCheck className="h-5 w-5" /><span className="label-caps">Hornsby Church School</span></div>
      <h1 className="mt-2 font-head text-4xl font-black">Cover School Enrollment</h1>
      <p className="mt-2 text-muted-foreground">$113 per family, per year · Alabama only. Your documents will be saved to your portal after payment.</p>

      <form onSubmit={submit} className="mt-8 space-y-10">
        <section>
          <h2 className="font-head text-xl font-bold">Parent / Guardian</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div><Label className="label-caps text-muted-foreground">Primary First Name *</Label><Input value={f.parent1_first} onChange={(e) => set("parent1_first", e.target.value)} className="mt-2 rounded-none bg-background" data-testid="cs-p1-first" /></div>
            <div><Label className="label-caps text-muted-foreground">Primary Last Name *</Label><Input value={f.parent1_last} onChange={(e) => set("parent1_last", e.target.value)} className="mt-2 rounded-none bg-background" data-testid="cs-p1-last" /></div>
            <div><Label className="label-caps text-muted-foreground">Secondary First Name</Label><Input value={f.parent2_first} onChange={(e) => set("parent2_first", e.target.value)} className="mt-2 rounded-none bg-background" data-testid="cs-p2-first" /></div>
            <div><Label className="label-caps text-muted-foreground">Secondary Last Name</Label><Input value={f.parent2_last} onChange={(e) => set("parent2_last", e.target.value)} className="mt-2 rounded-none bg-background" data-testid="cs-p2-last" /></div>
            <div><Label className="label-caps text-muted-foreground">Phone *</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 rounded-none bg-background" data-testid="cs-phone" /></div>
          </div>
        </section>

        <section>
          <h2 className="font-head text-xl font-bold">Home Address</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label className="label-caps text-muted-foreground">Street Address *</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} className="mt-2 rounded-none bg-background" data-testid="cs-address" /></div>
            <div><Label className="label-caps text-muted-foreground">City *</Label><Input value={f.city} onChange={(e) => set("city", e.target.value)} className="mt-2 rounded-none bg-background" data-testid="cs-city" /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="label-caps text-muted-foreground">State</Label><Input value={f.state} onChange={(e) => set("state", e.target.value)} className="mt-2 rounded-none bg-background" data-testid="cs-state" /></div>
              <div><Label className="label-caps text-muted-foreground">ZIP *</Label><Input value={f.zip} onChange={(e) => set("zip", e.target.value)} className="mt-2 rounded-none bg-background" data-testid="cs-zip" /></div>
              <div><Label className="label-caps text-muted-foreground">County</Label><Input value={f.county} onChange={(e) => set("county", e.target.value)} className="mt-2 rounded-none bg-background" data-testid="cs-county" /></div>
            </div>
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between">
            <h2 className="font-head text-xl font-bold">Student(s)</h2>
            <Button type="button" variant="outline" className="rounded-none label-caps" onClick={() => setStudents([...students, blankStudent()])} data-testid="cs-add-student"><Plus className="mr-2 h-4 w-4" /> Add Student</Button>
          </div>
          <div className="mt-4 space-y-4">
            {students.map((s, i) => (
              <div key={i} className="grid items-end gap-4 border border-border p-4 sm:grid-cols-[1fr_auto_1fr_auto]" data-testid={`cs-student-${i}`}>
                <div><Label className="label-caps text-muted-foreground">Full Name *</Label><Input value={s.name} onChange={(e) => setStudent(i, "name", e.target.value)} className="mt-2 rounded-none bg-background" data-testid={`cs-student-name-${i}`} /></div>
                <div><Label className="label-caps text-muted-foreground">Grade</Label>
                  <Select value={s.grade_level} onValueChange={(v) => setStudent(i, "grade_level", v)}>
                    <SelectTrigger className="mt-2 w-24 rounded-none bg-background" data-testid={`cs-student-grade-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">{GRADES.map((g) => <SelectItem key={g} value={g}>{g === "K" ? "K" : g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="label-caps text-muted-foreground">Date of Birth</Label><Input type="date" value={s.dob} onChange={(e) => setStudent(i, "dob", e.target.value)} className="mt-2 rounded-none bg-background" data-testid={`cs-student-dob-${i}`} /></div>
                {students.length > 1 && <Button type="button" variant="ghost" className="rounded-none text-destructive" onClick={() => setStudents(students.filter((_, idx) => idx !== i))} data-testid={`cs-remove-student-${i}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-head text-xl font-bold">Previous School <span className="text-sm font-normal text-muted-foreground">(optional — for your Records Request)</span></h2>
          <Input value={f.previous_school} onChange={(e) => set("previous_school", e.target.value)} placeholder="e.g. Slocomb Elementary School" className="mt-3 rounded-none bg-background" data-testid="cs-prev-school" />
        </section>

        <div className="border-t border-border pt-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="font-head text-2xl font-bold">Total: $113.00 <span className="text-sm font-normal text-muted-foreground">/ year</span></p>
            <Button type="submit" disabled={busy} className="rounded-none label-caps bg-primary px-8 py-6 hover:bg-primary/80" data-testid="cs-pay-btn">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Continue to Payment"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
